<template>
  <v-sheet class="pa-4 h-100">
    <v-sheet outlined class="rounded h-100 d-flex flex-column">
      <div
        class="pa-2 pb-0 d-flex align-center justify-space-between"
        style="gap: 8px"
        :class="{
          'flex-wrap': $vuetify.breakpoint.xs,
        }">
        <h3>Manage Users</h3>
        <v-tooltip bottom  v-has="permissions.Users.Create" >
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              v-has="permissions.Users.Create" 
              @click="update()" 
              small 
              color="primary"
              :disabled="isLimitReached"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon left>mdi-plus</v-icon> Add User
            </v-btn>
          </template>
          <span v-if="isLimitReached">You have reached the maximum limit of {{ company.usersLimitCount }} users</span>
          <span v-else-if="company.usersLimitCount === -1">Add a new user (unlimited users allowed)</span>
          <span v-else>Add a new user</span>
        </v-tooltip>

        <v-divider vertical class="mx-4"></v-divider>

        <v-btn-toggle v-model="activeUsers" mandatory color="primary" group>
          <v-btn
            :value="true"
            class="text-uppercase mr-0"
            small
            style="border-radius: 0.25rem"
            to="/users">
            <v-icon small v-if="activeUsers" left>mdi-check-circle</v-icon>
            <v-icon small v-else left>mdi-check-circle-outline</v-icon>

            Active Users
          </v-btn>
          <v-btn
            :value="false"
            class="text-uppercase"
            small
            style="border-radius: 0.25rem"
            to="/users-invitation"
            v-has="permissions.Users.Create">
            <v-icon small v-if="!activeUsers" left>mdi-share</v-icon>
            <v-icon small v-else left>mdi-share-outline</v-icon>

            Invited
          </v-btn>
        </v-btn-toggle>
        <v-spacer v-if="!$vuetify.breakpoint.xs"></v-spacer>
        <v-sheet :width="$vuetify.breakpoint.xs ? '100%' : 250">
          <v-text-field
            hide-details
            outlined
            v-model="options.search"
            dense
            class="search-bar"
            placeholder="Search in users..."
            clearable
            prepend-inner-icon="mdi-magnify"></v-text-field>
        </v-sheet>
      </div>
      <div class="subtitle-2 px-2 pb-2">
        users limit <v-chip x-small right>{{ company.usersLimitCount === -1 ? 'Unlimited' : company.usersLimitCount }}</v-chip>
      </div>
      <v-data-table
        class="elevation-0 pa-2 rounded-0 d-flex flex-column custom-table table-sticky"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-chevron-double-left',
          lastIcon: 'mdi-chevron-double-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
          itemsPerPageText: 'Row/Page',
          itemsPerPageOptions: [15, 30, 50, 100, 200, 300, 400, 500, -1],
        }"
        :headers="userHeaders"
        :items="entries"
        :options.sync="options"
        :loading="loading"
        :server-items-length="total"
        :item-class="row => row.isManagment ? 'grayed-out-row' : ''">
        <template v-slot:[`item.firstName`]="{ item }">
          <div class="d-inline-flex">
            <user-avatar :user="item" hide-roles disable-open-link></user-avatar>
          </div>
        </template>
        <template v-slot:[`item.email`]="{ item }">
          <a :href="'mailto:' + item.email">{{ item.email }}</a>
        </template>

        <template v-slot:[`item.roles`]="{ item }">
          <v-row style="margin: 5px 0px">
            <v-chip
              style="font-size: 11px; font-weight: 600"
              v-for="role in item.roles"
              :key="role.name"
              label
              x-small
              color="deep-purple"
              text-color="white"
              :ripple="false"
              link>
              {{ role.name }}
            </v-chip>
          </v-row>
        </template>
        <template v-slot:[`item.isActivated`]="{ item }">
          <span v-if="item.isActivated" class="green--text">
            <v-icon left>mdi-play</v-icon> Activated
          </span>
          <span v-else class="d-flex align-center opacity-40">
            <i class="fad fa-face-sleeping fs-18px mr-2"></i>Not Activated!
          </span>
        </template>
        <template v-slot:[`item.isSuspended`]="{ item }">
          <v-chip x-small :color="item.isSuspended ? 'error' : 'success'">
            <v-icon left x-small> {{ item.isSuspended ? 'mdi-block-helper' : 'mdi-check' }}</v-icon>
            {{ item.isSuspended ? 'Suspend' : 'Active' }}</v-chip
          >
        </template>
        <template v-slot:[`item.joinDate`]="{ item }">
          {{ $formatDate(item.joinDate) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu dense offset-x right v-if="!item.isManagment||company.companyType==CompanyType.Management">
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon elevation="0" v-bind="attrs" v-on="on">
                <v-icon size="16px">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list class="more-options-menu" dense>
              <v-list-item @click="update(item.id)" dense v-has="permissions.Users.Update">
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Update</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="suspend(item)" dense v-has="permissions.Users.Delete">
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="error">mdi-block-helper</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.isSuspended ? 'Reactive' : 'Suspend' }}</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="deleteUser(item)" dense v-has="permissions.Users.Delete">
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:no-data>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">No Data Available!</p>
          </div>
        </template>

        <template v-slot:loading>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">Loading Users...</p>
          </div>
        </template>
      </v-data-table>
      <EditUser ref="editUser"></EditUser>
    </v-sheet>
  </v-sheet>
</template>

<script>
  import UserAvatar from '@Share/components/UserAvatar.vue';
  import userHeaders from '@User/config/user.headers';
  import { mapGetters } from 'vuex';
  import EditUser from '@User/components/EditUser.vue';
  import permissions from '@Share/config/permissions';
  import { $formatDate } from '@Utils';
import { CompanyType } from '@/features/Share/config/enums';
  export default {
    name: 'user-list',
    data() {
      return {
        userHeaders,
        activeUsers: true,
        permissions,
        timerId: null,
        CompanyType:CompanyType
      };
    },
    computed: {
      ...mapGetters('USER', {
        loading: 'GET_LOADING',
        entries: 'GET_ENTRIES',
        total: 'GET_TOTAL',
        
      }),
   
      ...mapGetters('ACCOUNT', {
        company: 'GET_COMPANY',
        currentUser: 'GET_USER'
      }),
      options: {
        get() {
          return this.$store.getters['USER/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('USER/SET_OPTIONS', val);
        },
      },
      isLimitReached() {
        // If usersLimitCount is -1, it means unlimited users
        if (!this.company || !this.company.usersLimitCount) return false;
        return this.company.usersLimitCount !== -1 && 
               this.company.usersLimitUsed >= this.company.usersLimitCount;
      }
     
    },
    methods: {
      $formatDate,
      getDataDebounced() {
        if (this.timerId == null) {
          this.timerId = -1;
          this.$store.dispatch('USER/GET_MANAGEMENT');
          return;
        }
        // cancel pending call
        clearTimeout(this.timerId);

        // delay new call 400ms
        this.timerId = setTimeout(() => {
          this.$store.dispatch('USER/GET_MANAGEMENT');
        }, 400);
      },
      update(userItemId = null) {
        this.$refs.editUser.open(userItemId, true);
      },
      suspend(user) {
        this.$dialog.error({
          text: `Are You Sure You Want to ${user.isSuspended ? 'Reactive' : 'Suspend'} This User?`,
          title: `${user.isSuspended ? 'Reactive' : 'Suspend'} User`,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('USER/SUSPEND', {
                  id: user.id,
                  status: !user.isSuspended,
                });
              },
            },
          },
        });
      },
      deleteUser(user) {
        this.$dialog.error({
          text: `Are You Sure You Want to Delete This User?`,
          title: `Delete User`,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('USER/DELETE', user.id);
              },
            },
          },
        });
      },
    },
    watch: {
      options: {
        deep: true,
        handler() {
          this.getDataDebounced();
        },
      },
    },
    components: {
      UserAvatar,
      EditUser,
    },
  };
</script>

<style scoped>
.grayed-out-row {
  background-color: rgba(0, 0, 0, 0.12) !important;
  opacity: 0.8;
}
</style>
